// src/app/pages/sales-invoices/components/ItemSearchModal.tsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'; // [cite: 170]
import {
    Command,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '@/components/ui/command'; // [cite: 171]
import { useQuery } from '@tanstack/react-query'; // [cite: 172]
import { useDebounce } from '@/hooks/useDebounce'; // [cite: 172]
import { Loader2, AlertCircle } from 'lucide-react'; // [cite: 172]
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

// --- IndexedDB Integration ---
import { searchItemsAsync as searchItemsSQLite } from '@/lib/indexeddb/database';
import { isSQLiteAvailable } from '@/lib/indexeddb/integrationUtils';
import { useSQLiteSync } from '@/hooks/useSQLiteSync';
import { getItemCountAsync } from '@/lib/indexeddb/database';
import { useAuthStore } from '@/app/store/authStore';

// --- Configuration ---
// Set this flag to control search behavior: 
// true = use IndexedDB when available, false = always use API
const USE_INDEXEDDB_SEARCH = true;

// Define types for the component
export interface ItemSearchResult {
  id: number;
  name: string;
  code?: string | null;
  description?: string | null; // [cite: 174]
  stock?: number | null; // [cite: 174]
  tax_rate_id?: number | null; // Include tax_rate_id for calculating tax
  rate_decimal_places?: number | null; // Include rate_decimal_places for proper rate formatting
  [key: string]: unknown; // [cite: 174]
}

interface ItemSearchModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void; // [cite: 175]
  onSelectItem: (item: ItemSearchResult) => void; // [cite: 175]
  // API search function is now optional if using IndexedDB
  searchItems?: (query: string) => Promise<ItemSearchResult[]>;
  // External loading state might still be relevant
  isLoading?: boolean; // [cite: 175]
  initialSearchQuery?: string; // NEW: Initial search query to set when modal opens
}

export const ItemSearchModal: React.FC<ItemSearchModalProps> = ({
  isOpen,
  onOpenChange,
  onSelectItem,
  searchItems, // API search function
  isLoading: externalLoading = false,
  initialSearchQuery = '', // NEW: Accept initial search query with default empty string
}) => {
  const userOrgId = useAuthStore(state => state.userOrgId);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 200); // Increased debounce for IndexedDB
  const inputRef = useRef<HTMLInputElement>(null); // [cite: 177]
  const [selectedItemIndex, setSelectedItemIndex] = useState(0); // [cite: 177]
  const [indexedDBSearchResults, setIndexedDBSearchResults] = useState<ItemSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<Error | null>(null);
  const [itemCount, setItemCount] = useState<number>(0);

  // IndexedDB sync hook
  const {
    isInitialized: isIndexedDBInitialized,
    isSyncing,
    syncData,
    lastSyncTime,
    needsDataSync
  } = useSQLiteSync();

  // Check item count when modal opens
  useEffect(() => {
    if (isOpen && isIndexedDBInitialized && userOrgId) {
      getItemCountAsync(userOrgId).then(count => {
        setItemCount(count);
        console.log("[ItemSearchModal] Local item count:", count);
      }).catch(err => {
        console.error("[ItemSearchModal] Error getting item count:", err);
        setItemCount(0);
      });
    }
  }, [isOpen, isIndexedDBInitialized, lastSyncTime, userOrgId]); // Re-check after sync

  // --- IndexedDB Search Logic ---
  useEffect(() => {
    // Only search when we have a query and modal is open
    if (USE_INDEXEDDB_SEARCH && isOpen && debouncedSearchQuery.length > 1 && isSQLiteAvailable()) {
      const performIndexedDBSearch = async () => {
        setIsSearching(true);
        setSearchError(null);
        
        try {
          console.log("[ItemSearchModal] Searching IndexedDB for:", debouncedSearchQuery);
          
          if (!userOrgId) {
            console.error("[ItemSearchModal] No organization ID available");
            return;
          }
          const results = await searchItemsSQLite(debouncedSearchQuery, userOrgId, 50);
          console.log("[ItemSearchModal] IndexedDB search results:", results.length, "items");
          
          // Convert LocalItem to ItemSearchResult format
          const convertedResults: ItemSearchResult[] = results.map(item => ({
            id: item.id,
            name: item.name,
            code: item.sku || null,
            description: item.description,
            tax_rate_id: item.tax_rate_id,
            rate_decimal_places: item.rate_decimal_places || 0,
            // Add stock if you have it in your data
          }));
          
          setIndexedDBSearchResults(convertedResults);
        } catch (error) {
          console.error("[ItemSearchModal] IndexedDB search error:", error);
          setIndexedDBSearchResults([]);
          setSearchError(error instanceof Error ? error : new Error("Unknown search error"));
        } finally {
          setIsSearching(false);
        }
      };
      
      performIndexedDBSearch();
    } else if (USE_INDEXEDDB_SEARCH) {
      // Clear results if query is too short or IndexedDB not available
      setIndexedDBSearchResults([]);
    }
  }, [debouncedSearchQuery, isOpen]);

  // --- API Search Query ---
  const {
    data: apiSearchResults,
    isLoading: isApiLoading,
    isError: isApiError,
    error: apiError,
  } = useQuery({
    queryKey: ['itemSearch', debouncedSearchQuery],
    queryFn: () => {
        if (!searchItems) {
            console.warn("API search function (`searchItems`) is not provided.");
            return Promise.resolve([]);
        }
        return searchItems(debouncedSearchQuery);
    },
    // Enable API query only if not using IndexedDB OR if IndexedDB is not available
    enabled: isOpen && (!USE_INDEXEDDB_SEARCH || !isSQLiteAvailable()) && debouncedSearchQuery.length > 1 && !!searchItems,
    staleTime: 1000 * 60 * 5, // Cache results for 5 minutes
  });

  // --- Determine Final Results and Loading State ---
  // Use IndexedDB if enabled and available
  const usingIndexedDB = USE_INDEXEDDB_SEARCH && isSQLiteAvailable();
  const searchResults = usingIndexedDB ? indexedDBSearchResults : apiSearchResults;
  const isLoading = usingIndexedDB ? isSearching : (externalLoading || isApiLoading);
  const isError = usingIndexedDB ? !!searchError : isApiError;
  const error = usingIndexedDB ? searchError : apiError;
  
  // Check if we should show sync prompt
  const shouldShowSyncPrompt = USE_INDEXEDDB_SEARCH && isIndexedDBInitialized && itemCount === 0 && !isSyncing;

  // Focus input and reset selection on modal open
  useEffect(() => {
    if (isOpen) {
      // Set initial search query if provided
      if (initialSearchQuery) {
        setSearchQuery(initialSearchQuery);
      }
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
      setSelectedItemIndex(0);
    } else {
       // Clear search query when modal closes
       setSearchQuery('');
       if (USE_INDEXEDDB_SEARCH) setIndexedDBSearchResults([]); // Clear results too
    }
  }, [isOpen, initialSearchQuery]); // [cite: 179] modification

  // Reset selection when results change
  useEffect(() => {
    setSelectedItemIndex(0);
  }, [searchResults]); // [cite: 180]

  const handleSelect = (item: ItemSearchResult): void => {
    onSelectItem(item);
    onOpenChange(false); // [cite: 182]
    // No need to clear query here, handled by isOpen effect
  }; //

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.nativeEvent.isComposing) return; // [cite: 183]

    if (searchResults && searchResults.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setSelectedItemIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : prev
        ); // [cite: 185]
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setSelectedItemIndex(prev => (prev > 0 ? prev - 1 : 0)); // [cite: 187]
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (selectedItemIndex >= 0 && selectedItemIndex < searchResults.length) {
          handleSelect(searchResults[selectedItemIndex]); // [cite: 189]
        }
      }
    }

    if (event.key === 'Escape') {
      onOpenChange(false); // [cite: 190]
    }
  }; //

  // Determine search type label for the dialog title
  const searchTypeLabel = usingIndexedDB ? 'Local' : 'Online';

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px] p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onKeyDown={handleKeyDown} // [cite: 191]
      >
        <Command shouldFilter={false}> {/* Disable Command's built-in filtering */}
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>Search Products / Services ({searchTypeLabel})</DialogTitle> {/* [cite: 192] modification */}
          </DialogHeader>
          <CommandInput
            ref={inputRef}
            placeholder="Type item name, code, or description..."
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="h-11 mx-4 my-2" // [cite: 192]
          />

          <CommandList className="max-h-[400px] p-4 pt-0 overflow-y-auto"> {/* [cite: 193] */}
            {shouldShowSyncPrompt && (
              <Alert className="m-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="space-y-2">
                  <p>No items found in local database. Sync data to enable fast local search.</p>
                  <Button 
                    onClick={() => {
                      console.log('[ItemSearchModal] Sync button clicked');
                      syncData();
                    }} 
                    disabled={isSyncing}
                    size="sm"
                    className="w-full"
                  >
                    {isSyncing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      'Sync Now'
                    )}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {isLoading && (
              <div className="p-4 text-center">
                <Loader2 className="h-6 w-6 animate-spin inline-block"/>
              </div> // [cite: 193]
            )}

            {!isLoading && debouncedSearchQuery.length > 1 && (!searchResults || searchResults.length === 0) && (
              <CommandEmpty>No products found.</CommandEmpty> // [cite: 195]
            )}

            {isError && <div className="p-4 text-red-600">Error: {(error as Error)?.message || 'Could not fetch items'}</div>} {/* [cite: 196] */}

            <CommandGroup>
              {searchResults?.map((item, index) => (
                <CommandItem
                  key={item.id}
                  // Use a unique value for cmdk internal keying if names/codes aren't unique
                  value={`item-${item.id}-${item.name}`} // Changed value to be more unique
                  onSelect={() => handleSelect(item)}
                  className={`flex justify-between items-center cursor-pointer p-2 rounded-md ${index === selectedItemIndex ? 'bg-accent' : ''}`} // [cite: 197] modified className
                  // Scroll into view logic (optional but recommended for keyboard navigation)
                  ref={el => {
                    if (el && index === selectedItemIndex) {
                        el.scrollIntoView({ block: 'nearest' });
                    }
                  }}
                >
                  <div>
                    <span className="font-medium">{item.name}</span> {/* [cite: 198] */}
                    {item.code && <span className="text-sm text-muted-foreground ml-2">({item.code})</span>} {/* [cite: 198] */}
                    {item.description && <div className="text-xs text-muted-foreground mt-1">{item.description}</div>} {/* [cite: 198] */}
                  </div>
                  {item.stock !== undefined && <span className="text-sm">{item.stock} in stock</span>} {/* [cite: 199] */}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  ); // [cite: 200]
};